"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.attachmentEachWithSort = exports.everyNth = void 0;
/**
 * Can be wrapped around a template to execute on every nth item - in the example it is making a grid with 2 items per row.
 * @param { Array<Record<string, any>> } listToDisplay List to have the wrapped template applied to
 * @param { number } every Increment for how frequently you want to execute the wrapped template
 * @param { Record<string, any> } options Options include ability to run list in reverse by passing {"inverse": true}
 * @example {{#everyNth subcontractor_use.images 2}}
 *            {{#if isModZeroNotFirst}}
 *                    </div>
 *            {{/if}}
 *            {{#if isModZero}}
 *                    <div class="TEMPLATE__tmGridRow">
 *            {{/if}}
 *            <div class="TEMPLATE__tmGridItem">
 *                    <a href="{{ this.url }}" target="_blank">
 *                            <img class="TEMPLATE__tmImage" src="{{this.url}}" type="image"/>
 *                    </a>
 *            </div>
 *            {{#if isLast}}
 *                    </div>
 *            {{/if}}
 * {{/everyNth}}
 */
const everyNth = (listToDisplay, every, options) => {
    const fn = options === null || options === void 0 ? void 0 : options.fn;
    const inverse = options === null || options === void 0 ? void 0 : options.inverse;
    let ret = "";
    if ((listToDisplay === null || listToDisplay === void 0 ? void 0 : listToDisplay.length) > 0) {
        listToDisplay.forEach((listItem, idx) => {
            const modZero = idx % every === 0;
            ret =
                ret +
                    fn(Object.assign({}, listItem, {
                        isModZero: modZero,
                        isModZeroNotFirst: modZero && idx > 0,
                        isLast: idx === listToDisplay.length - 1,
                    }));
        });
    }
    else {
        ret = inverse(this);
    }
    return ret;
};
exports.everyNth = everyNth;
/**
 * Sorting helper to sort images by name. Not an exported helper.
 * @ignore
 * @param { Record<string, any> } a record to be compared
 * @param { Record<string, any> } b second record to be compared
 * @returns { number } sort int
 * @example sortByName({"name": "Bob"}, {"name": "Sally"}) => 1
 * @example sortByName({"name": "Terrence"}, {"name": "Sally"}) => -1
 * @example sortByName({"name": "Bob"}, {"name": "Bob"}) => 0
 */
const sortByName = (a, b) => {
    a.name = a.name || "";
    b.name = b.name || "";
    if (a.name > b.name) {
        return 1;
    }
    else if (a.name < b.name) {
        return -1;
    }
    return 0;
};
/**
 * Sorting helper to sort images by image type. Not an exported helper
 * @ignore
 * @param { Record<string, any> } a record to be compared
 * @param { Record<string, any> } b second record to be compared
 * @returns { number } sort int
 * @example sortByType({"type": "image/png"}, {"type": "text/csv"}) => 1
 * @example sortByType({"type": "text/csv"}, {"type": "image/png"}) => -1
 * @example sortByType({"type": "text/csv"}, {"type": "text/csv"}) => 0
 */
const sortByType = (a, b) => {
    a.type = a.type || "image/"; // default to "image/" since old stores didn't store the type
    b.type = b.type || "image/"; // default to "image/" since old stores didn't store the type
    if (a.type.startsWith("image/") && !b.type.startsWith("image/")) {
        return 1;
    }
    else if (!a.type.startsWith("image/") && b.type.startsWith("image/")) {
        return -1;
    }
    return 0;
};
/**
 * Helper to sort store attachments by name, alphabetically, followed by type (non-image vs image)
 * @param { any[] } array - the array to sort
 * @param { any } options - handlebars options
 * @returns { string } - the handlebars string
 * @example {{#attachmentEachWithSort subcontractor_use.images}}
 *  <div class="TEMPLATE__tmGridItem">
 *      <a href="{{ this.url }}?filename={{this.name}}" target="_blank">
 *          <img
 *              class="TEMPLATEV2__tmImage"
 *              src="{{this.url}}"
 *              type="image"
 *          />
 *      </a>
 *  </div>
 *  {{/attachmentEachWithSort}}
 */
const attachmentEachWithSort = (array, options) => {
    array.sort(sortByName).sort(sortByType);
    let output = "";
    for (let i = 0, j = array.length; i < j; i++) {
        output += options.fn(array[i]);
    }
    return output;
};
exports.attachmentEachWithSort = attachmentEachWithSort;
